import { defineComponent } from "vue";
import propertiesPanelModule from "bpmn-js-properties-panel";
import propertiesProviderModule from "bpmn-js-properties-panel/lib/provider/camunda";
import BpmnModeler from "bpmn-js/lib/Modeler";
import camundaModdleDescriptor from "camunda-bpmn-moddle/resources/camunda.json";
import minimapModule from "diagram-js-minimap";
import initPalette from "@mv/bpmn-properties-palette/dist/index";
import * as generate from "nanoid/generate";
import { DiagramToDocument } from "@mv/flow-shared-utils/functions/bpmn-diagram";
import { changeCategory, deployDefinition, loadDefinitionDiagram, loadProcessDefinition, validateXMLImport } from "@/store/flow/actions";
import dataProvider from "@/utils/dataProvider";
import customTranslate from "./modeler-i18n/translation";

type savedDefinitionInfo = { id: string; key: string };

export default defineComponent({
  name: "sdk-flow-definition-builder",
  components: {
    "sdk-flow-task-form": () => import("../sdk-flow-task-form/sdk-flow-task-form.vue"),
    "sdk-flow-candidate-groups": () => import("../sdk-flow-candidate-groups/sdk-flow-candidate-groups.vue"),
  },
  props: {
    definitionId: String,
    definitionKey: String,
    categoryId: String,
  },
  data() {
    return {
      showFormSubscription: null as any,
      showCandidateGroupsSubscription: null as any,
      bpmnModeler: null as any,
      bpmnEditorId: `id_${this.$generateId(10)}`,
      propertiesPanelId: `id_${this.$generateId(10)}`,
      showPanel: false,
      showGroupsPanel: false,
      currentTaskForm: "",
      savedDefinition: {} as savedDefinitionInfo,
      loading: false,
      showStartTimerChangedDialog: false,
      oldStartEventConfig: "",
    };
  },
  methods: {
    generatedId() {
      const alphabet = "abcdefghijklmnopkrstuvwxzABCDEFGHIJKLMNOPKRSTUVWXZ";
      const id = generate(alphabet, 3) + generate(`0123456789_${alphabet}`, 19);
      return id;
    },
    async importXml() {
      const { bpmn20Xml } = await this.$dispatch(loadDefinitionDiagram)([{ id: this.definitionId, key: this.definitionKey }]);
      this.saveXmlToCanvas(bpmn20Xml);
      this.getStartEventProperties(bpmn20Xml);
    },
    async importBlankXml() {
      const blankXml = `<?xml version="1.0" encoding="UTF-8"?><bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:camunda="http://camunda.org/schema/1.0/bpmn" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" id="Definitions_1" targetNamespace="http://bpmn.io/schema/bpmn" exporter="Camunda Modeler" exporterVersion="1.11.2"><bpmn:process id="${this.generatedId()}" name="novo-processo" isExecutable="true" camunda:versionTag="1.0.0" /><bpmndi:BPMNDiagram id="BPMNDiagram_1"><bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="novo-processo" /></bpmndi:BPMNDiagram></bpmn:definitions>`;
      this.saveXmlToCanvas(blankXml);
    },
    async updateProcessId(xml: string, keepCurrentId = false, currentId: string | null = null) {
      let newXML = "";
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, "text/xml");

      const allItems = xmlDoc.getElementsByTagName("bpmn:process");
      for await (const item of allItems) {
        const oldId = item.getAttribute("id") as string;
        const newId = keepCurrentId ? currentId : this.generatedId();
        const searchRegExp = new RegExp(oldId, "g");
        const targetVar = newXML.length ? newXML : xml;
        newXML = targetVar.replace(searchRegExp, newId);
      }

      this.saveXmlToCanvas(newXML);
    },
    prepareProcessImport(xml: string) {
      this.bpmnModeler.saveXML({ format: true }, async (saveError: any, currentXML: any) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(currentXML, "text/xml");
        const currentId = xmlDoc.getElementsByTagName("bpmn:process")[0].getAttribute("id");
        this.updateProcessId(xml, true, currentId);
      });
    },
    saveXmlToCanvas(xml: string) {
      this.bpmnModeler.importXML(xml, async (err: any) => {
        if (err) {
          throw err;
        }
      });
    },
    getStartEventProperties(xml: string, saveToState = true) {
      const Diagram = DiagramToDocument(xml);
      const startEventId = Diagram.getStartEvent();
      if (startEventId) {
        const timerEventValue = Diagram.getTimerDefinition(startEventId);
        if (timerEventValue) {
          if (saveToState) {
            this.oldStartEventConfig = timerEventValue;
          } else {
            return timerEventValue;
          }
        }
      }
    },
    async validateImport(xml: string) {
      this.loading = true;
      await this.$dispatch(validateXMLImport)([xml])
        .then(() => {
          this.prepareProcessImport(xml);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async importXMLFromFile(e: any) {
      if (!e.target.files || !e.target.files.length) {
        return;
      }

      const fileReader = new FileReader();
      fileReader.onload = (evt: any) => {
        if (evt.target.readyState !== 2) {
          return;
        }
        if (evt.target.error) {
          throw new Error("Error while reading file");
        }
        if (evt.target.result) {
          this.validateImport(evt.target.result as string);
        }
      };
      fileReader.readAsText(e.target.files[0]);
    },
    getNextVersion() {
      if (!this.definitionId) {
        return 1;
      }

      return this.savedDefinition.id ? Number(this.savedDefinition.id.split(":")[1]) + 1 : Number(this.definitionId.split(":")[1]) + 1;
    },
    incrementVersionTag(xml: string) {
      const latestVersion = this.getNextVersion();

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, "text/xml");
      xmlDoc.getElementsByTagName("bpmn:process")[0].setAttribute("camunda:versionTag", latestVersion.toString());
      const newXML = new XMLSerializer().serializeToString(xmlDoc);
      return newXML;
    },
    getDefinitionName(xml: any) {
      return xml.match(/<bpmn:process id="(.+?)" name="(.+?)"/)[2];
    },
    /** Método que irá gravar as informações necessários para realizar a navegação ao sair do processo */
    setCreatedDefinition(definitionId: string, key: string) {
      this.savedDefinition.id = definitionId;
      this.savedDefinition.key = key;
    },
    closeChangesConfirmDialog() {
      this.showStartTimerChangedDialog = false;
    },
    saveDefinitionHandler(confirmed = false) {
      if (this.bpmnModeler) {
        this.bpmnModeler.saveXML({ format: true }, async (err: any, xml: any) => {
          const formattedXml = this.incrementVersionTag(xml);
          const processId = (formattedXml.match(/<bpmn:process id="(.+?)" name/) as RegExpMatchArray)[1];

          const startEventConfiguartion = this.getStartEventProperties(formattedXml, false);
          if (startEventConfiguartion && startEventConfiguartion !== this.oldStartEventConfig) {
            if (confirmed) {
              this.closeChangesConfirmDialog();
              this.deployProcessDefinition(formattedXml, processId);
            } else {
              this.showStartTimerChangedDialog = true;
            }
          } else {
            this.deployProcessDefinition(formattedXml, processId);
          }
        });
      }
    },
    async deployProcessDefinition(xml: string, processId: string) {
      this.loading = true;
      await this.$dispatch(deployDefinition)([xml, processId], this.$t("flow.definitionBuilder.notifications.success"))
        .then(async (createdDefinition: any) => {
          this.getStartEventProperties(xml, true);
          const definitionId = Object.keys(createdDefinition.deployedProcessDefinitions)[0];
          const key = createdDefinition.deployedProcessDefinitions[definitionId].key;
          this.setCreatedDefinition(definitionId, key);

          await this.$dispatch(loadProcessDefinition)([definitionId]);
          await this.$dispatch(changeCategory)([this.categoryId, key]);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goBack() {
      const confirmExit = confirm(this.$t("flow.definitionBuilder.notifications.exitAlert") as string);

      if (!confirmExit) {
        return;
      }

      if (Object.keys(this.savedDefinition).length) {
        this.$router.push({
          name: "flow-definition",
          params: {
            definitionId: this.savedDefinition.id,
            definitionKey: this.savedDefinition.key,
            categoryId: this.categoryId as string,
          },
        });
      } else {
        this.$router.back();
      }
    },
    hideFormsPanel() {
      this.showPanel = false;
      this.currentTaskForm = "";
    },
    hideGroupsPanel() {
      this.showGroupsPanel = false;
    },
    showFormsPanel({ detail }: CustomEvent) {
      this.currentTaskForm = detail;
      this.showPanel = true;
    },
    showCandidateGroupsPanel() {
      this.showGroupsPanel = true;
    },
    switchPalette() {
      const confirmExit = confirm("Suas alterações não salvas serão perdidas. Deseja continuar?");

      if (!confirmExit) {
        return;
      }

      const currentPalette = localStorage.getItem("current-bpmn-palette");
      currentPalette === "custom" ? localStorage.setItem("current-bpmn-palette", "native") : localStorage.setItem("current-bpmn-palette", "custom");
      window.location.reload();
    },
    buildModeler() {
      const customTranslateModule = {
        translate: ["value", customTranslate],
      };

      const paletteModules: any = {
        native: [propertiesProviderModule, propertiesPanelModule, minimapModule, customTranslateModule],
        custom: [propertiesProviderModule, minimapModule, customTranslateModule],
      };

      const paletteType: any = localStorage.getItem("current-bpmn-palette") || "custom";
      localStorage.setItem("current-bpmn-palette", paletteType);

      const modelerConfig: any = {
        container: `#${this.bpmnEditorId}`,
        additionalModules: paletteModules[paletteType],
        moddleExtensions: {
          camunda: camundaModdleDescriptor,
        },
        keyboard: { bindTo: window },
      };

      if (paletteType === "native") {
        modelerConfig.propertiesPanel = { parent: `#${this.propertiesPanelId}` };
      }

      this.bpmnModeler = new BpmnModeler(modelerConfig);
    },
  },
  async mounted() {
    this.buildModeler();
    const currentPalette = localStorage.getItem("current-bpmn-palette");
    if (currentPalette === "custom") {
      initPalette(this.bpmnModeler, document.getElementById(this.propertiesPanelId), dataProvider, this.$i18n.locale);
    }
    this.showFormSubscription = this.showFormsPanel.bind(this);
    this.showCandidateGroupsSubscription = this.showCandidateGroupsPanel.bind(this);
    document.body.addEventListener("forms:show", this.showFormSubscription);
    document.body.addEventListener("candidateGroups:show", this.showCandidateGroupsSubscription);

    if (this.definitionId) {
      this.importXml();
    } else {
      this.importBlankXml();
    }

    const eventBus = this.bpmnModeler.get("eventBus");
    eventBus.on("element.click", (e: any) => {
      if (e.element && e.element.type === "bpmn:Process") {
        this.hideFormsPanel();
        this.hideGroupsPanel();
      }
    });
  },
  beforeDestroy() {
    document.body.removeEventListener("forms:show", this.showFormSubscription, true);
    document.body.removeEventListener("forms:show", this.showCandidateGroupsSubscription, true);
  },
});
